import { useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { PlanEditProvider } from '@/features/individual-payment/context/PlanEditContext';
import { useCreatePlan } from '@/features/individual-payment/hooks/useCreatePlan';
import type { PlanFormValues } from '@/features/individual-payment/hooks/usePlanFormSchema';
import {
  buildPlanCreateDefaults,
  planCreateSchema,
} from '@/features/individual-payment/hooks/usePlanFormSchema';
import { PlanLinkSection } from '@/features/individual-payment/ui/molecules/PlanLinkSection';
import { PlanDetailsTab } from '@/features/individual-payment/ui/organisms/PlanDetailsTab';
import { PlanValuesTab } from '@/features/individual-payment/ui/organisms/PlanValuesTab';
import type { PlanCreateTab } from '@/features/individual-payment/ui/templates/PlanCreateTemplate';
import {
  PLAN_CREATE_TABS,
  PlanCreateTemplate,
} from '@/features/individual-payment/ui/templates/PlanCreateTemplate';
import { buildPlanCreatePayload } from '@/features/individual-payment/utils/buildPlanCreatePayload';
import { findTabWithError } from '@/features/individual-payment/utils/planCreateErrorTab';

/**
 * A aba ativa vive na search param, como na edição: sobrevive à recarga e ao
 * botão voltar, e é linkável.
 */
const searchSchema = z.object({
  tab: z.enum(['detalhes', 'valores']).default('detalhes'),
  /**
   * Pessoa já escolhida na origem — a criação aberta pelo menu de um contrato
   * sabe de quem é. Ambos opcionais: pelo botão do cabeçalho a tela abre vazia.
   *
   * `personId` chega como os dígitos do código de pessoa. O formulário guarda a
   * chave textual do lookup (`"USR260953"`), e `parseStudentKeyId` aceita as duas
   * formas — o prefixo é opcional na extração.
   */
  personId: z.string().optional(),
  personName: z.string().optional(),
});

/**
 * Valida o `tab` vindo da URL sem confiar no schema da rota.
 *
 * O valor cru é `unknown` de propósito: se o `validateSearch` não tiver sido
 * aplicado, `search.tab` pode chegar `undefined` ou como qualquer string.
 */
function isPlanCreateTab(value: unknown): value is PlanCreateTab {
  return typeof value === 'string' && (PLAN_CREATE_TABS as readonly string[]).includes(value);
}

export const Route = createFileRoute('/_authenticated/individual-payment/create/')({
  validateSearch: searchSchema,
  component: IndividualPaymentPlanCreatePage,
});

/**
 * Página de criação de um plano financeiro individual.
 *
 * Duas abas sobre um formulário só, como na edição — o salvamento é um `POST`
 * único, então trocar de aba não descarta nada. O bloco de vínculo fica acima
 * delas: é ele que condiciona o conteúdo das duas.
 *
 * As seções editáveis são as **mesmas** da edição: o `POST` e o `PUT` recebem o
 * mesmo `IndividualPaymentSaveViewModel`, então o formulário é compartilhado e o
 * `mode` do contexto decide o que não existe antes de o contrato existir.
 */
export function IndividualPaymentPlanCreatePage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const search = Route.useSearch();

  // A aba renderizada é estado local, com a URL como espelho — mesmo arranjo da
  // edição. Depender só do search param travaria a tela na primeira aba se o
  // parâmetro não voltasse validado.
  const urlTab = isPlanCreateTab(search?.tab) ? search.tab : 'detalhes';
  const [tab, setTab] = useState<PlanCreateTab>(urlTab);

  // Sincroniza quando a URL muda por fora do clique: deep link, voltar, avançar.
  useEffect(() => {
    setTab(urlTab);
  }, [urlTab]);

  // Pessoa vinda da origem, quando houver. Lida uma vez: `defaultValues` só é
  // consultado na montagem, e reagir a mudanças aqui sobrescreveria o que o
  // operador tivesse escolhido depois.
  const presetPersonId = typeof search?.personId === 'string' ? search.personId : '';
  const presetPersonName = typeof search?.personName === 'string' ? search.personName : '';

  const form = useForm<PlanFormValues>({
    resolver: zodResolver(planCreateSchema),
    defaultValues: {
      ...buildPlanCreateDefaults(),
      ...(presetPersonId === '' ? {} : { personId: presetPersonId }),
    },
  });

  const { create, isPending, isForbidden, isBusinessError, businessMessage } = useCreatePlan();

  const goToTab = (next: PlanCreateTab) => {
    // Estado primeiro: a aba troca sem esperar o router.
    setTab(next);
    void navigate({ to: '/individual-payment/create', search: { tab: next } });
  };

  const handleSave = () => {
    void form.handleSubmit(
      values => {
        create(buildPlanCreatePayload(values), {
          onSuccess: financialPersonId => {
            alert({ alertType: 'success', title: t('planCreate.createSuccess') });

            // O plano recém-criado ainda não tem cupons nem documentos de
            // convênio, e é na edição que essas seções existem — devolver à
            // listagem obrigaria o operador a pesquisar de novo o aluno que
            // acabou de cadastrar.
            if (financialPersonId !== null) {
              void navigate({
                to: '/individual-payment/$financialPersonId',
                params: { financialPersonId: String(financialPersonId) },
                search: { tab: 'detalhes' },
              });
              return;
            }

            // Sem id não há para onde navegar com segurança: a listagem é o
            // destino que não depende de adivinhar um `financialPersonId`.
            void navigate({ to: '/individual-payment' });
          },
          onError: error => {
            // `5xx` não tem tratamento global — o interceptor só repassa o erro.
            // Sem este alerta, uma falha do backend deixava a tela como estava e
            // o operador não saberia se o plano foi criado ou não.
            if (error.kind === 'server') {
              alert({ alertType: 'danger', title: t('planCreate.serverError') });
            }
          },
        });
      },
      errors => {
        // Com o formulário dividido em abas, um erro na aba inativa deixaria o
        // botão parecendo quebrado: nada muda na tela. Levar o operador até ele
        // é o que mantém a validação visível.
        const target = findTabWithError(Object.keys(errors));
        if (target !== null && target !== tab) goToTab(target);
      },
    )();
  };

  // A mensagem do backend é a única explicação disponível para as regras de
  // criação — o frontend não as conhece. Trocá-la por um texto genérico deixaria
  // o operador sem saber o que corrigir.
  const errorMessage = isBusinessError ? (businessMessage ?? t('planCreate.createError')) : null;

  return (
    <PlanEditProvider
      mode="create"
      form={form}
      editor={undefined}
      financialPersonId={null}
      isSaving={isPending}
      isReadOnly={isForbidden}
    >
      <PlanCreateTemplate
        activeTab={tab}
        onTabChange={goToTab}
        linkSection={
          <>
            {isForbidden ? (
              <div className="flex flex-col gap-quark rounded-sm border-hairline border-base-gray-300 bg-base-gray-100 px-xxs py-micro">
                <span className="ts-paragraph-xs !font-semibold text-ctx-content-title">
                  {t('planCreate.forbidden.title')}
                </span>
                <span className="ts-paragraph-xxs text-base-gray-700">
                  {t('planCreate.forbidden.hint')}
                </span>
              </div>
            ) : null}

            {errorMessage !== null ? (
              <div className="flex flex-col gap-quark rounded-sm border-hairline border-base-danger-300 bg-base-danger-100 px-xxs py-micro">
                <span className="ts-paragraph-xs text-base-danger-700">{errorMessage}</span>
              </div>
            ) : null}

            <PlanLinkSection initialPersonLabel={presetPersonName} />
          </>
        }
        onSave={handleSave}
        onCancel={() => void navigate({ to: '/individual-payment' })}
      >
        {tab === 'detalhes' ? <PlanDetailsTab /> : null}
        {tab === 'valores' ? <PlanValuesTab /> : null}
      </PlanCreateTemplate>
    </PlanEditProvider>
  );
}
